import "./assets/App.css";
import { ReactElement, useEffect } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import theme from "./assets/theme";
import Home from "./pages/Home";
import RoutesList from "./routes";
import Header from "./components/Header";
import Footer from "./Footer";
import { Hreflang } from "./components/Hreflang";
import Feedback from "./pages/Feedback";
import Credits from "./pages/Credits";

const queryClient = new QueryClient();

const SUPPORTED_LANGS = ["en", "el"] as const;
const DEFAULT_LANG = "en";

const isSupported = (lang?: string) =>
  SUPPORTED_LANGS.includes(lang as "en" | "el");

function LocalizedSite() {
  const { lng } = useParams();
  const { i18n } = useTranslation();
  const location = useLocation();
  const routes = RoutesList();

  useEffect(() => {
    if (lng && isSupported(lng) && i18n.language !== lng)
      i18n.changeLanguage(lng);
  }, [lng, i18n]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (!isSupported(lng)) return <Navigate to={`/${DEFAULT_LANG}`} replace />;

  return (
    <>
      <Hreflang />
      <Header routes={routes} />
      <Routes>
        <Route index element={<Home />} />
        {routes.map(
          (route) =>
            "route" in route && (
              <Route
                key={route.name}
                path={route.route.replace(/^\//, "")}
                element={route.component}
              />
            ),
        )}
        <Route path="feedback" element={<Feedback />} />
        <Route path="credits" element={<Credits />} />
        <Route path="*" element={<Navigate to={`/${lng}`} replace />} />
      </Routes>
      <Footer />
    </>
  );
}

export default function App(): ReactElement {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Routes>
            <Route
              path="/"
              element={<Navigate to={`/${DEFAULT_LANG}`} replace />}
            />
            <Route path="/:lng/*" element={<LocalizedSite />} />
            <Route
              path="*"
              element={<Navigate to={`/${DEFAULT_LANG}`} replace />}
            />
          </Routes>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}
